'use client';
import clsx from 'clsx';
import { forwardRef, useId } from 'react';

import { textVariants } from '../../text';
import { RequiredSpan } from '../required';

import Checkbox from './checkbox';
import { CheckboxVariant, type CheckboxGroupProps } from './checkbox.types';

const CheckboxGroup = forwardRef<HTMLInputElement, CheckboxGroupProps>(
  (
    {
      heading,
      options,
      checked,
      variant = CheckboxVariant.LEFT,
      disabled,
      required,
      showHeading,
      onChange,
      hasError,
    },
    ref
  ) => {
    const uniqueId = useId();

    if (options.length === 0) {
      return null;
    }
    return (
      <div
        ref={ref}
        role="group"
        aria-labelledby={`checkbox-group-label-${uniqueId}`}
      >
        <div className="flex flex-col gap-d md:gap-c lg:gap-b">
          {heading ? (
            <label
              id={`checkbox-group-label-${uniqueId}`}
              className={clsx(
                showHeading
                  ? textVariants({ variant: 'body2', weight: 'semi' })
                  : 'sr-only',
                { 'text-error': hasError }
              )}
            >
              {heading} {required ? <RequiredSpan /> : null}
            </label>
          ) : null}

          {options.map((option) => (
            <Checkbox
              name={option.id.toString()}
              key={option.id.toString()}
              label={option.label}
              checked={checked.includes(option.id)}
              variant={variant}
              onChange={() => {
                onChange(option.id);
              }}
              {...((option.disabled || disabled) && {
                disabled: option.disabled || disabled,
              })}
              {...(option.description && { description: option.description })}
              hasError={hasError}
            />
          ))}
        </div>
      </div>
    );
  }
);

CheckboxGroup.displayName = 'Checkbox Group';

export default CheckboxGroup;
